import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import { isArray, mergeWith, values } from 'lodash';
import { Action } from 'redux';
import { Observable, of } from 'rxjs';

import { showEditPageItemNameEditorErrors } from '../../../actions/editPageItemNameEditor';
import { updateRateCorrectionsEditorValidation } from '../../../actions/rateCorrectionsEditor';
import { updateTargetTagsEditorValidation } from '../../../actions/targetTagsEditor';
import { mapStringToCurrencySymbol } from '../../../mappers/common/common';
import { selectEditPageItemNameEditorValue } from '../../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectGroupValidationParams } from '../../../selectors/groupEdit/group/group';
import { ErrorsEdit, ValidationErrorData } from '../../../typings/common';
import { CampaignType } from '../../../typings/domain';
import { CurrencyCode, SaveAdGroup } from '../../../typings/graphql-grid';
import { GroupId, GroupsCategories } from '../../../typings/groups';
import {
    EditGroupData,
    EditGroupDataValue,
    EditInternalGroupDataValue,
    EditPerformanceGroupDataValue,
    EditTextGroupDataValue
} from '../../../typings/groupsEditState';
import { State } from '../../../typings/state';
import { TargetPlatform, TargetTagsEditorState } from '../../../typings/targetTagsEditor';
import { EditPageBackToFrontErrorMapper } from '../../EditPageBackToFrontErrorMapper/EditPageBackToFrontErrorMapper';
import { extractValidationErrors } from '../../ErrorHelpers/ErrorHelpers';
import { formatNumber } from '../../FormatHelpers/FormatHelpers';
import { GroupsHelpers } from '../../GroupsHelpers/GroupsHelpers';
import { validateRateCorrectionEditor } from '../../RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import {
    createContentPromotionGroupAddHandleErrorMapper,
    createMcBannerGroupUpdateHandleErrorMapper,
    createSmartGroupUpdateHandleErrorMapper,
    createTextGroupAddHandleErrorMapper,
    createTextGroupUpdateHandleErrorMapper
} from '../GroupsBackToFrontErrorMappers';

import * as keyset from './ClientGroupEditErrors.i18n';

const i18n = i18nFactory(keyset);

export interface EditGroupValidationParams {
    minGeneralPrice: number;
    maxGeneralPrice: number;
    currency: CurrencyCode;
}

type ClientErrorGroupKeys = EditTextGroupDataValue & EditPerformanceGroupDataValue & EditInternalGroupDataValue;

export interface ClientErrorsConfigRule<K extends keyof ClientErrorGroupKeys> {
    code: string;
    text: string;
    validate: (value: ClientErrorGroupKeys[K]) => boolean;
    needToValidate?: (group: EditGroupDataValue, value: ClientErrorGroupKeys[K]) => boolean;
}

interface ClientErrorGroupRules<K extends keyof ClientErrorGroupKeys> {
    rules: Array<ClientErrorsConfigRule<K>>;
}

const getClientErrorsConfig = (
    params: EditGroupValidationParams
): Partial<{ [K in keyof ClientErrorGroupKeys]: ClientErrorGroupRules<K> }> => {
    return {
        feedId: {
            rules: [
                {
                    code: 'required',
                    validate: (value: string): boolean => {
                        return Boolean(value);
                    },
                    needToValidate: (group: EditGroupDataValue): boolean => {
                        return group.campaignType === CampaignType.PERFORMANCE;
                    },
                    text: i18n('feed-required')
                }
            ]
        },
        name: {
            rules: [
                {
                    code: 'required',
                    validate: (value: string): boolean => {
                        return value.trim() !== '';
                    },
                    text: i18n('name-empty')
                }
            ]
        },
        generalRate: {
            rules: [
                {
                    code: 'maxValue',
                    validate: (value: number): boolean => {
                        return value <= params.maxGeneralPrice;
                    },
                    needToValidate: (group: EditGroupDataValue, value: number): boolean => {
                        return (
                            (group.campaignType === CampaignType.TEXT ||
                                group.campaignType === CampaignType.CPM_BANNER ||
                                group.campaignType === CampaignType.MCBANNER ||
                                group.campaignType === CampaignType.CONTENT_PROMOTION) &&
                            value !== undefined
                        );
                    },
                    text: i18n('general-rate-max-price', {
                        maxValue: formatNumber(params.maxGeneralPrice),
                        currency: mapStringToCurrencySymbol(params.currency)
                    })
                },
                {
                    code: 'minValue',
                    validate: (value: number): boolean => {
                        return value >= params.minGeneralPrice;
                    },
                    needToValidate: (group: EditGroupDataValue, value: number): boolean => {
                        return (
                            (group.campaignType === CampaignType.TEXT ||
                                group.campaignType === CampaignType.CPM_BANNER ||
                                group.campaignType === CampaignType.MCBANNER ||
                                group.campaignType === CampaignType.CONTENT_PROMOTION) &&
                            value !== undefined
                        );
                    },
                    text: i18n('general-rate-min-price', {
                        minValue: formatNumber(params.minGeneralPrice),
                        currency: mapStringToCurrencySymbol(params.currency)
                    })
                }
            ]
        }
    };
};

export interface EditGroupFieldErrorsData {
    errors: Array<ValidationErrorData>;
}

export const getGroupFieldValidationErrors = (
    group: EditGroupData,
    fieldName: string,
    params: EditGroupValidationParams
): Array<ValidationErrorData> => {
    const clientValidationConfig = getClientErrorsConfig(params);

    return clientValidationConfig[fieldName].rules.reduce(
        (errors: Array<ValidationErrorData>, rule: ClientErrorsConfigRule<'name'>) => {
            const fieldValue = group.value[fieldName];

            if (
                (rule.needToValidate === undefined || rule.needToValidate(group.value, fieldValue)) &&
                rule.validate(fieldValue) === false
            ) {
                errors.push({
                    path: `groups[${group.id}].${fieldName}`,
                    code: rule.code,
                    text: rule.text
                });
            }

            return errors;
        },
        []
    );
};

export const getGroupNameEditorValidationResult = (state: State, groupId: string): ValidationErrorData[] => {
    const validationParams = selectGroupValidationParams(state);
    const {
        editGroups: { groups },
        editPageItemNameEditor
    } = state;
    const editorData = selectEditPageItemNameEditorValue(
        editPageItemNameEditor,
        GroupsHelpers.getGroupEditPageItemEditorId(groupId)
    );
    const newGroupData = {
        ...groups[groupId],
        value: {
            ...state.editGroups.groups[groupId].value,
            name: editorData.value
        }
    };

    return getGroupFieldValidationErrors(newGroupData, 'name', validationParams);
};

export const validateTargetTagsEditor = (editorState: TargetTagsEditorState): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];
    const targetPlatformList = values(editorState.platforms);

    const somePlatformsSelected = targetPlatformList.some((platform: TargetPlatform) => platform.selected);

    if (!somePlatformsSelected) {
        errors.push({
            code: 'target-platform-required',
            text: i18n('target-platform-required')
        });
    }

    return errors;
};

export const getGroupsOpenedEditorsValidationResult = (
    groups: Array<EditGroupData>,
    state: State
): { valid: boolean; showErrorsActions: Array<Observable<Action>> } => {
    let isValid = true;
    const showErrorsActions: Array<Observable<Action>> = [];

    groups.forEach((group: EditGroupData) => {
        const groupId = group.id;
        const editorId = GroupsHelpers.getGroupEditPageItemEditorId(groupId);

        const groupNameEditorData = selectEditPageItemNameEditorValue(state.editPageItemNameEditor, editorId);

        if (groupNameEditorData.isVisible) {
            const groupNameEditorErrors = getGroupNameEditorValidationResult(state, groupId);

            if (groupNameEditorErrors.length !== 0) {
                isValid = false;

                showErrorsActions.push(of(showEditPageItemNameEditorErrors(editorId, groupNameEditorErrors)));
            }
        }

        if (
            group.value.campaignType &&
            [
                CampaignType.TEXT,
                CampaignType.CPM_BANNER,
                CampaignType.PERFORMANCE,
                CampaignType.MCBANNER,
                CampaignType.CONTENT_PROMOTION
            ].includes(group.value.campaignType) &&
            state.rateCorrectionsEditor[groupId] &&
            state.rateCorrectionsEditor[groupId].isVisible
        ) {
            const rateCorrectionValidationResult = validateRateCorrectionEditor(
                state.rateCorrectionsEditor[groupId].value
            );

            if (!rateCorrectionValidationResult.isValid) {
                isValid = false;
                showErrorsActions.push(
                    of(updateRateCorrectionsEditorValidation(groupId, rateCorrectionValidationResult.errors))
                );
            }
        }

        if (
            group.value.campaignType &&
            [CampaignType.CPM_BANNER].includes(group.value.campaignType) &&
            state.targetTagsEditor[editorId]
        ) {
            const targetPlatformsErrors = validateTargetTagsEditor(state.targetTagsEditor[editorId]);

            if (targetPlatformsErrors.length) {
                isValid = false;
                showErrorsActions.push(of(updateTargetTagsEditorValidation(editorId, targetPlatformsErrors)));
            }
        }
    });

    return {
        valid: isValid,
        showErrorsActions
    };
};

export const getSaveGroupsValidationErrors = (
    response: FetchResult<SaveAdGroup>,
    groupsCategoryGroupIdsMap: Record<GroupsCategories, GroupId[]>
): ErrorsEdit => {
    if (!response.data) {
        throw new Error('internal server error');
    }

    const {
        addTextAdGroups,
        updateTextAdGroup,
        updateSmartAdGroups,
        updateMcBannerAdGroup,
        addContentPromotionAdGroups,
        updateContentPromotionAdGroups
    } = response.data;

    const customizer = (objValue: Object, srcValue: Object): Object | undefined => {
        if (isArray(objValue)) {
            return objValue.concat(srcValue);
        }
        return undefined;
    };

    const groupsCategoryResponseMap = {
        [GroupsCategories.textAdd]: addTextAdGroups,
        [GroupsCategories.textUpdate]: updateTextAdGroup,
        [GroupsCategories.smartUpdate]: updateSmartAdGroups,
        [GroupsCategories.mcBannerUpdate]: updateMcBannerAdGroup,
        [GroupsCategories.contentPromotionAdd]: addContentPromotionAdGroups,
        [GroupsCategories.contentPromotionUpdate]: updateContentPromotionAdGroups
    };

    const groupsCategoryErrorsMapperMap = {
        [GroupsCategories.textAdd]: (groupIds: Array<GroupId>): EditPageBackToFrontErrorMapper =>
            createTextGroupAddHandleErrorMapper(groupIds),
        [GroupsCategories.textUpdate]: (groupIds: Array<GroupId>): EditPageBackToFrontErrorMapper =>
            createTextGroupUpdateHandleErrorMapper(groupIds),
        [GroupsCategories.smartUpdate]: (groupIds: Array<GroupId>): EditPageBackToFrontErrorMapper =>
            createSmartGroupUpdateHandleErrorMapper(groupIds),
        [GroupsCategories.mcBannerUpdate]: (groupIds: Array<GroupId>): EditPageBackToFrontErrorMapper =>
            createMcBannerGroupUpdateHandleErrorMapper(groupIds),
        [GroupsCategories.contentPromotionAdd]: (groupIds: Array<GroupId>): EditPageBackToFrontErrorMapper =>
            createContentPromotionGroupAddHandleErrorMapper(groupIds),
        [GroupsCategories.contentPromotionUpdate]: (groupIds: Array<GroupId>): EditPageBackToFrontErrorMapper =>
            createContentPromotionGroupAddHandleErrorMapper(groupIds)
    };

    return Object.keys(groupsCategoryResponseMap).reduce((errors: ErrorsEdit, groupCategoryKey: GroupsCategories) => {
        const groupResponse = groupsCategoryResponseMap[groupCategoryKey];

        if (!groupResponse) {
            return errors;
        }

        const [hasErrors, serverErrors] = extractValidationErrors(groupResponse);

        if (!hasErrors) {
            return errors;
        }

        const groupCategoryIds = groupsCategoryGroupIdsMap[groupCategoryKey];
        const groupCategoryErrorsMapper = groupsCategoryErrorsMapperMap[groupCategoryKey](groupCategoryIds);
        const groupCategoryErrors = groupCategoryErrorsMapper.mapServerErrorsToClientError(serverErrors);

        return mergeWith(errors, groupCategoryErrors, customizer);
    }, {});
};
